import { createContext, useContext, useEffect, useMemo, useState } from "react";

import { CommandPalette } from "../components/CommandPalette";

/**
 * The platform search box, and the one key that opens it.
 *
 * ## Why a provider rather than a component somebody drops in
 *
 * There are four apps in this bundle and each builds its own sidebar, so a
 * palette owned by any one of them is a palette the other three have to
 * re-implement -- which is exactly how the CRM ended up with a ⌘K that only
 * searched companies while the Helpdesk had none at all. This sits in
 * `AppFrame`, the single frame all four render inside, so the shortcut works
 * everywhere by construction and there is one place it can be got wrong.
 *
 * The context exists for the second way in: the CRM's sidebar has a search
 * *button*, and a button in a sidebar passed to `AppFrame` as a prop cannot be
 * handed a callback without threading one through all four shells. `useCommandPalette()`
 * is what lets a sidebar open the thing without knowing who owns it.
 *
 * ## The listener is here and not in the dialog
 *
 * A shortcut that only works while its dialog is mounted is not a shortcut.
 * The provider is always mounted; the dialog is rendered only while open, so
 * its own state -- the term, the highlighted row -- resets between uses, which
 * is what somebody expects from a box they open to ask one question.
 */
interface CommandPaletteApi {
  open: () => void;
}

const Context = createContext<CommandPaletteApi>({ open: () => {} });

export function useCommandPalette(): CommandPaletteApi {
  return useContext(Context);
}

export function CommandPaletteProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);

  /*
   * ⌘K on a Mac, Ctrl-K elsewhere -- both accepted on both, because a desk
   * running two operating systems should not have two shortcuts.
   *
   * `preventDefault` because Ctrl-K is "focus the address bar" in Firefox and
   * a search box that also navigates the browser is worse than no shortcut.
   *
   * Deliberately no check for whether the focus is in a text field, unlike the
   * Helpdesk's "/" shortcut. A bare letter has to yield to typing; a modifier
   * combination does not, and somebody half-way through a reply who wants to
   * look a company up is precisely who this is for.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const api = useMemo(() => ({ open: () => setOpen(true) }), []);

  return (
    <Context.Provider value={api}>
      {children}
      {open ? <CommandPalette onClose={() => setOpen(false)} /> : null}
    </Context.Provider>
  );
}
